import { useDebounce } from '@agro-club/agroclub-shared'
import { useAProgress } from 'hooks/useAProgress'
import { HedgeType } from 'modules/domain/export/types'
import { FormikFieldsType } from 'modules/domain/types'
import { endpoints } from 'modules/endpoints'
import { RequestError } from 'modules/errors'
import { apiClient } from 'modules/utils/httpClient'
import { useEffect, useRef, useState } from 'react'
import { HedgingFields } from './CommonOptionFields'

const DEBOUNCE_DELAY = 1000

export const useFormNetRate = (formik: FormikFieldsType<HedgingFields>, initNetRate?: number) => {
  const [netRate, setNetRate] = useState<number | null>(null)
  const { option_volume, strike, option_bonus } = formik.values
  const debouncedOptVolume = useDebounce(option_volume, DEBOUNCE_DELAY)
  const debouncedStrike = useDebounce(strike, DEBOUNCE_DELAY)
  const debouncedOptBonus = useDebounce(option_bonus, DEBOUNCE_DELAY)
  const isForwardType = formik.values.hedge_type === HedgeType.forward

  const [progress, getAndSetNetRate] = useAProgress<{ net_rate: number }>(
    (optVolume: number, strike: number, optBonus: number) =>
      apiClient.get(endpoints.exportHedgesNetRate(optVolume, strike, optBonus)),
    {
      eventName: 'netRate',
      onSuccess: (res) => setNetRate(res.net_rate),
      onError: (error) => {
        const { errors } = error as RequestError
        formik.setErrors(errors)
        setNetRate(null)
      },
    },
  )

  const isInitialMount = useRef(true)
  useEffect(() => {
    if (isForwardType) return

    if (isInitialMount.current && initNetRate) {
      setNetRate(initNetRate)
      isInitialMount.current = false
      return
    }

    if (debouncedOptVolume && debouncedStrike && debouncedOptBonus) {
      getAndSetNetRate(debouncedOptVolume, debouncedStrike, debouncedOptBonus)
    } else {
      setNetRate(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedOptVolume, debouncedStrike, debouncedOptBonus, isForwardType, initNetRate])

  return { netRateProgress: progress, netRate }
}
